import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { message } from "antd";

import {
    createGroup,
    createUserApi,
    editUserApi,
    getGroupsApi,
    getMessages,
    getUserApi,
    getUsersApi,
    updateGroup,
    getGroupsUsersApi,
    manageUsersGroupsApi
} from "./chatBortApi";

const initialState = {
    loading: false,

    users: [],
    paginationUsers: {},

    singleUser: null,

    messages: [],
    paginationMessages: {},

    groups: [],
    paginationGroups: {},
};

const actions = {
    CREATE_USER: "chat/createUser",
    EDIT_USER: "chat/editUser",
    GET_USERS: "chat/getUsers",

    GET_USER_BY_ID: "chat/getUserById",
    GET_MESSAGES: "chat/getMessages",
    MANAGE_GROUPUSERS: "chat/managegroupusers",

    GET_GROUPS: "chat/getGroups",
    CREATE_GROUP: "chat/createGroup",
    UPDATE_GROUP: "chat/updateGroup",
    GROUPS_USERS: "chat/getgroupusers"
};

/* ------------------------------------------------------
 * ASYNC THUNKS
 * ------------------------------------------------------ */
export const createUserThunk = createAsyncThunk(
    actions.CREATE_USER,
    async (payload) => await createUserApi(payload)
);

export const editUserThunk = createAsyncThunk(
    actions.EDIT_USER,
    async (payload) => await editUserApi(payload)
);

export const getUsersThunk = createAsyncThunk(
    actions.GET_USERS,
    async (payload) => await getUsersApi(payload)
);
export const assignedUsersThunk = createAsyncThunk(
    actions.MANAGE_GROUPUSERS,
    async (payload) => await manageUsersGroupsApi({
        params: { groupId: payload.groupId }, body: {
            unlinkAssigned: payload.unlinkAssigned,
            notAssigned: payload.notAssigned,
            groupName:payload.groupName
        }
    })
);
export const getGroupUsersThunk = createAsyncThunk(
    actions.GROUPS_USERS,
    async (payload) => await getGroupsUsersApi(payload)
);
export const getUserByIdThunk = createAsyncThunk(
    actions.GET_USER_BY_ID,
    async (payload) => await getUserApi(payload)
);

export const getMessagesThunk = createAsyncThunk(
    actions.GET_MESSAGES,
    async (payload) => await getMessages(payload)
);

export const getGroupsThunk = createAsyncThunk(
    actions.GET_GROUPS,
    async (payload) => await getGroupsApi(payload)
);

export const createGroupThunk = createAsyncThunk(
    actions.CREATE_GROUP,
    async (payload) => await createGroup(payload)
);

export const updateGroupThunk = createAsyncThunk(
    actions.UPDATE_GROUP,
    async (payload) => await updateGroup(payload)
);

/* ------------------------------------------------------
 * SLICE
 * ------------------------------------------------------ */
const chatBortSlice = createSlice({
    name: "chatBort",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        /* ---------------------- CREATE USER ---------------------- */
        builder
            .addCase(createUserThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(createUserThunk.fulfilled, (state, action) => {
                const { status, message: msg } = action.payload;

                status === "success"
                    ? message.success("User created successfully!")
                    : message.error(msg);

                state.loading = false;
            })
            .addCase(createUserThunk.rejected, (state, action) => {
                message.error(action.error.message);
                state.loading = false;
            });

        /* ---------------------- EDIT USER ---------------------- */
        builder
            .addCase(editUserThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(editUserThunk.fulfilled, (state, action) => {
                const { status, message: msg } = action.payload;

                status === "success"
                    ? message.success("User updated successfully!")
                    : message.error(msg);

                state.loading = false;
            })
            .addCase(editUserThunk.rejected, (state, action) => {
                message.error(action.error.message);
                state.loading = false;
            });

        /* ---------------------- GET USERS (Matches Your Backend) ---------------------- */
        builder
            .addCase(getUsersThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUsersThunk.fulfilled, (state, action) => {
                const { status, data, currentPage, totalPages, totalRecords, limit } =
                    action.payload;

                if (status === "success") {
                    state.users = data;
                    state.paginationUsers = {
                        currentPage,
                        totalPages,
                        totalRecords,
                        limit,
                    };
                }

                state.loading = false;
            })
            .addCase(getUsersThunk.rejected, (state, action) => {
                message.error(action.error.message);
                state.loading = false;
            });

        /* ---------------------- GET SINGLE USER ---------------------- */
        builder
            .addCase(getUserByIdThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUserByIdThunk.fulfilled, (state, action) => {
                const { status, data } = action.payload;

                if (status === "success") state.singleUser = data;

                state.loading = false;
            })
            .addCase(getUserByIdThunk.rejected, (state, action) => {
                message.error(action.error.message);
                state.loading = false;
            });

        /* ---------------------- GET MESSAGES ---------------------- */
        builder
            .addCase(getMessagesThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getMessagesThunk.fulfilled, (state, action) => {
                const { status, data, pagination } = action.payload;

                if (status === "success") {
                    state.messages = data;
                    state.paginationMessages = pagination;
                }

                state.loading = false;
            })
            .addCase(getMessagesThunk.rejected, (state, action) => {
                message.error(action.error.message);
                state.loading = false;
            });

        /* ---------------------- GET GROUPS ---------------------- */
        builder
            .addCase(getGroupsThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getGroupsThunk.fulfilled, (state, action) => {
                const { status, data, pagination } = action.payload;

                if (status === "success") {
                    state.groups = data;
                    state.paginationGroups = pagination;
                }

                state.loading = false;
            })
            .addCase(getGroupsThunk.rejected, (state, action) => {
                message.error(action.error.message);
                state.loading = false;
            });

        /* ---------------------- CREATE GROUP ---------------------- */
        builder
            .addCase(createGroupThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(createGroupThunk.fulfilled, (state, action) => {
                const { status, message: msg } = action.payload;

                status === "success"
                    ? message.success(msg)
                    : message.error(msg);

                state.loading = false;
            })
            .addCase(createGroupThunk.rejected, (state, action) => {
                message.error(action.error.message);
                state.loading = false;
            });

        /* ---------------------- UPDATE GROUP ---------------------- */
        builder
            .addCase(updateGroupThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateGroupThunk.fulfilled, (state, action) => {
                const { status, message: msg } = action.payload;

                status === "success"
                    ? message.success(msg)
                    : message.error(msg);

                state.loading = false;
            })
            .addCase(updateGroupThunk.rejected, (state, action) => {
                message.error(action.error.message);
                state.loading = false;
            });
    },
});
export const chatBortActions = chatBortSlice.actions;
export default chatBortSlice.reducer;
